import { useMemo, useState } from 'react';
import {
  AritoIcon,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ConfirmationDialogV2,
  ErpDataTableV2,
  ErpInputTableV2,
  LoadingOverlayV2,
  BulkDeleteDialogV4
} from '@/components/custom/erp';
import { getSupplierReturnReceiptColumns, getSupplierReturnReceiptDetailColumns } from './cols-definition';
import { PhieuXuatTraLaiNhaCungCapProvider, usePhieuXuatTraLaiNhaCungCapContext } from './context';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { initialSearchValues, SearchFormValues } from './schema';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { useAuth } from '@/contexts/auth-context';

import { useSearchTable } from './hooks';
import { ExcelConfig } from './utils';

function PhieuXuatTraLaiNhaCungCapPageContent() {
  const {
    export: exportContext,
    forms,
    crud,
    patch,
    search,
    import: importContext,
    transactionOptions
  } = usePhieuXuatTraLaiNhaCungCapContext();
  const { isExporting } = exportContext;
  const { isImportDialogOpen, closeImportDialog } = importContext;
  const {
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    showForm,
    showDelete,
    formMode,
    handleCloseForm,
    handleCloseDelete,
    handleViewClick,
    clearSelection,
    showBulkDelete,
    handleCloseBulkDelete,
    handleAddClick,
    handleEditClick,
    handleDeleteClick,
    handleCopyClick,
    isCopyMode
  } = forms;
  const {
    data,
    isLoading,
    totalItems,
    currentPage,
    handlePageChange,
    deleteItem,
    detail,
    refreshData,
    sumTotal,
    addItem,
    updateItem,
    searchData,
    isFiltered
  } = crud;
  const { patchItem } = patch;
  const { showSearchDialog, handleSearchClose } = search;
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const { searchState, handleSearchChange, onPageChange, hasFilterParams, handleInitialSearch, handleRefreshClick } =
    useSearchTable({
      searchData,
      searchParams,
      setSearchParams,
      loading: isLoading,
      handleViewClick,
      sumTotal,
      isFiltered,
      handlePageChange
    });

  const { entityUnit } = useAuth();
  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const handleSearchSubmit = async (filters: SearchFormValues) => {
    setSearchParams(filters);
    await handleInitialSearch(filters);
  };

  const resolvedRows = hasFilterParams ? searchState.searchResults : data;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedSumTotal = hasFilterParams ? (searchState.totalRow?.t_tt_nt ?? sumTotal) : sumTotal;
  const mainColumns = useMemo(
    () => getSupplierReturnReceiptColumns(handleViewClick, resolvedSumTotal, isLoading),
    [handleViewClick, resolvedSumTotal, isLoading]
  );

  const handleDelete = async () => {
    if (!selectedObj) return;
    (selectedObj as MuaHangRecord).chi_tiet_data?.forEach((item: MuaHangRecord) => {
      patchItem(item.line_hd.uuid, { sl_cl: Number(item.line_hd.sl_cl) + Number(item.so_luong) });
    });
    await deleteItem(selectedObj.uuid);

    handleCloseDelete();
    handleCloseForm();
    clearSelection();
    refreshData();
  };

  const handleImportBodies = async (bodies: MuaHangRecord[]) => {
    try {
      let success = 0;
      let failed = 0;

      for (const body of bodies) {
        try {
          await addItem(body);
          success++;
        } catch (error: MuaHangRecord) {
          console.error('Create error:', error);
          failed++;
        }
      }

      if (success === 0) {
        toast({
          description: 'Không thể nhập phiếu xuất trả lại. Vui lòng kiểm tra dữ liệu và thử lại.',
          variant: 'destructive'
        });
      } else {
        toast({
          description:
            failed === 0
              ? 'Đã nhập phiếu xuất trả lại từ Excel thành công'
              : 'Đã nhập phiếu xuất trả lại từ Excel, một số chứng từ có lỗi',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    } finally {
      await refreshData();
      setIsImporting(false);
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    setIsImporting(true);

    if (!entityUnit?.uuid) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      setIsImporting(false);
      return;
    }

    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      setIsImporting(false);
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {
        entityUnitUUID: entityUnit.uuid,
        defaultMaGD: transactionOptions[0]?.value || 'TL'
      }
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
      closeImportDialog();
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
      closeImportDialog();
    }
    setIsImporting(false);
  };

  const tables = [
    {
      name: 'Tất cả',
      rows: resolvedRows,
      columns: mainColumns
    },
    {
      name: 'Chưa ghi sổ',
      rows: resolvedRows?.filter(row => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: resolvedRows?.filter(row => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã ghi sổ',
      rows: resolvedRows?.filter(row => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    },
    {
      name: 'Khác',
      rows: resolvedRows?.filter(row => row.status === '99'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-black' />
    }
  ];

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col overflow-hidden'>
      {showSearchDialog && (
        <SearchDialog open={showSearchDialog} onClose={handleSearchClose} onSearch={handleSearchSubmit} />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={closeImportDialog}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showForm && (
        <FormDialog
          onClose={handleCloseForm}
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
          addItem={addItem}
          updateItem={updateItem}
          refreshData={refreshData}
          patchItem={patchItem}
        />
      )}

      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={() => {
            handleDelete();
          }}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialogV4
          title='Phiếu xuất trả lại nhà cung cấp'
          onClose={handleCloseBulkDelete}
          columns={mainColumns}
          dataRows={data}
          handleDelete={deleteItem}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onRefreshClick={async () => {
              if (hasFilterParams) {
                await handleRefreshClick();
              } else if (isFiltered) {
                await searchData(searchParams);
              } else {
                await refreshData();
              }
            }}
          />

          {
            <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
              <ResizablePanelGroup direction='vertical' className='h-full overflow-hidden'>
                <ResizablePanel defaultSize={50} minSize={20}>
                  <div data-perf-id='pxtl-ncc-master-table' className='h-full w-full overflow-hidden'>
                    <ErpDataTableV2
                      tables={tables as MuaHangRecord}
                      onRowClick={handleRowClick}
                      totalItems={resolvedTotalItems}
                      currentPage={resolvedCurrentPage}
                      onPageChange={onPageChange}
                      selectedRowId={selectedRowIndex || undefined}
                      onSearchChange={handleSearchChange}
                      pageSize={20}
                      loading={isLoading || isImporting}
                    />
                  </div>
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={50} minSize={20}>
                  <div data-perf-id='pxtl-ncc-bottom-detail-table' className='h-full overflow-hidden'>
                    <ErpInputTableV2
                      rows={selectedObj && !isLoading ? detail?.chi_tiet || [] : []}
                      columns={getSupplierReturnReceiptDetailColumns}
                      showIndexColumn
                      actionsDisabled={formMode === 'view' || !selectedObj}
                      height={'100%'}
                      loading={isLoading || isImporting}
                    />
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          }
        </>
      )}
    </div>
  );
}

export default function PhieuXuatTraLaiNhaCungCapPage() {
  return (
    <PhieuXuatTraLaiNhaCungCapProvider>
      <PhieuXuatTraLaiNhaCungCapPageContent />
    </PhieuXuatTraLaiNhaCungCapProvider>
  );
}
